import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlan } from './PlanContext';
import { usePermissions } from './PermissionsContext';
import { useModuleInstallation } from '../hooks/useModuleInstallation';
import {
    WORKSPACES,
    createCustomModuleWorkspace,
    createDynamicWorkspace,
    isWorkspaceActive as isWorkspaceActiveUtil,
} from '../config/workspaces';

const WORKSPACE_KEY = 'crm_active_workspace';
// The Wiki rail entry returns to the last Wiki view, like the prototype —
// leaving the Wiki for the CRM and coming back should not reset you to Home.
const WIKI_LAST_PATH_KEY = 'crm_wiki_last_path';

/**
 * Transform provider-declared navigation into workspace sections.
 * Root items become sections with a direct path.
 * Group items become sections with child items.
 * All items are tagged with `moduleNav: true` so the sidebar
 * uses custom click handling instead of React Router NavLink.
 *
 * The `navigation` field from the API is an object with `items`:
 *   { version: 1, items: [ {id, label, type: 'root'|'group', path, icon, items?} ] }
 */
function transformNavigationToSections(nav) {
    if (!nav?.items) return [];

    return nav.items.map((item) => {
        if (item.type === 'root') {
            return {
                id: item.id,
                label: item.label,
                path: item.path,
                icon: item.icon,
                moduleNav: true,
            };
        }
        if (item.type === 'group') {
            return {
                id: item.id,
                label: item.label,
                icon: item.icon,
                moduleNav: true,
                items: (item.items || []).map((child) => ({
                    id: child.id,
                    label: child.label,
                    path: child.path,
                    icon: child.icon,
                    moduleNav: true,
                })),
            };
        }
        return null;
    }).filter(Boolean);
}

/**
 * Generate dynamic workspace objects from the API provider list and
 * enrich them with provider-declared navigation. Native workspaces
 * (CRM Core) keep their static sections.
 *
 * @param {Array} providers - Provider objects from /v1/modules/providers
 * @returns {Array} - Array of workspace objects (dynamic only)
 */
function generateDynamicWorkspaces(providers) {
    if (!providers || providers.length === 0) return [];

    return providers.map((provider) => {
        const ws = createDynamicWorkspace(provider);
        // Enrich with provider-declared navigation (sidebar sections)
        if (provider.navigation) {
            ws.sections = transformNavigationToSections(provider.navigation);
        }
        return ws;
    });
}

/**
 * Build workspaces for installed custom modules (plan Task 7.8).
 *
 * The collision rule: **Core wins, then first-installed.** A custom module whose
 * `navigation.id` collides with a Core or provider workspace is dropped rather than
 * merged — merging two workspaces' sections would produce a sidebar belonging to neither,
 * and letting the module win would let an installed bundle replace CRM Core's own rail
 * tile. A hue collision is cosmetic rather than structural, so it is nudged instead of
 * rejected: the module still works, it just gets a distinguishable identity colour.
 *
 * Both cases log, because a module silently missing from the rail is the hardest kind of
 * install failure to diagnose from the outside.
 *
 * @param {Array} customModules - entries from /v1/modules/custom
 * @param {Array} existing - workspaces already in the merged list
 */
function generateCustomModuleWorkspaces(customModules, existing) {
    if (!customModules || customModules.length === 0) return [];

    const takenIds = new Set(existing.map(ws => ws.id));
    const takenHues = new Set(existing.map(ws => (ws.hue || '').toLowerCase()));
    const out = [];

    for (const module of customModules) {
        const ws = createCustomModuleWorkspace(module);
        if (!ws) {
            // A backend-only module — an event handler and an API, no UI. Legitimate,
            // and it correctly has no rail presence.
            continue;
        }
        if (takenIds.has(ws.id)) {
            console.warn(
                `[workspaces] custom module '${module.slug}' declares navigation.id `
                + `'${ws.id}', which is already taken. It will not appear in the rail. `
                + 'Change navigation.id in its manifest and resubmit the bundle.',
            );
            continue;
        }
        const hue = (ws.hue || '').toLowerCase();
        if (hue && takenHues.has(hue)) {
            console.warn(
                `[workspaces] custom module '${module.slug}' uses hue ${ws.hue}, already `
                + 'in use. Nudging it so the two workspaces stay distinguishable.',
            );
            ws.hue = nudgeHue(ws.hue);
        }
        takenIds.add(ws.id);
        takenHues.add((ws.hue || '').toLowerCase());
        out.push(ws);
    }
    return out;
}

/**
 * Shift a hex hue enough to be visibly distinct without leaving its colour family.
 *
 * Rotating far would put the module in a different visual category than its author chose,
 * which is a worse outcome than two similar tiles — the point is only to make them
 * distinguishable at a glance in the rail.
 */
function nudgeHue(hex) {
    const raw = hex.replace('#', '');
    const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
    const value = parseInt(full, 16);
    if (Number.isNaN(value)) return hex;
    const shift = (channel) => Math.min(255, Math.max(0, channel + 40));
    const r = shift((value >> 16) & 0xff);
    const g = shift((value >> 8) & 0xff);
    const b = shift(value & 0xff);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
    const { checkModule, checkFeature, planUnknown } = usePlan();
    const { can, permissionsUnknown } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();

    // Module provider installation state — fetched once here and shared
    // via context so RuntimeShell, WorkspaceRail, and WorkspaceHub don't
    // each make duplicate API calls.
    const { isInstalled, getHealthStatus, providers, customModules, loading } = useModuleInstallation();

    // Module navigation target — when the user clicks a module-nav sidebar item,
    // this state holds the path + nonce that ModuleIframe watches to trigger
    // iframe navigation via ProviderShell.
    const [moduleNavTarget, setModuleNavTarget] = useState(null);

    // Merge static native workspaces (CRM Core + coming-soon placeholders)
    // with dynamically-generated module provider workspaces from the API.
    // This is the single source of truth for all workspace rendering.
    const enrichedWorkspaces = useMemo(() => {
        // 1. Start with static workspaces (CRM Core + coming-soon placeholders).
        //    CRM Core keeps its hardcoded sidebar sections.
        const staticWorkspaces = WORKSPACES.map(ws => ({ ...ws }));

        // 2. Generate dynamic workspaces from the API provider list.
        //    Each provider gets a workspace with navigation enriched from
        //    the provider's manifest-declared navigation plan.
        const dynamicWorkspaces = generateDynamicWorkspaces(providers);

        // 3. Merge dynamic providers without allowing a provider to replace a
        //    built-in workspace. In particular, provider_id/navigation.id `wiki`
        //    must not shadow the native Wiki rail entry or its permission gate.
        const merged = [...staticWorkspaces];
        for (const dynWs of dynamicWorkspaces) {
            const existingIdx = merged.findIndex(ws => ws.id === dynWs.id);
            if (existingIdx >= 0) {
                if (merged[existingIdx].type === 'native') {
                    console.warn(`[workspaces] provider '${dynWs.id}' collides with a built-in workspace; keeping the built-in workspace.`);
                    continue;
                }
                merged[existingIdx] = dynWs;
            } else {
                merged.push(dynWs);
            }
        }

        // 4. Custom modules last (plan Task 7.8). Last because the collision rule is
        //    "Core wins, then first-installed": appending means an id already claimed by
        //    Core or a provider is seen as taken, so the module is dropped rather than
        //    replacing something — the opposite of step 3, where a provider deliberately
        //    overwrites its own static placeholder.
        merged.push(...generateCustomModuleWorkspaces(customModules, merged));

        return merged;
    }, [providers, customModules]);

    // Dynamic promotion: a workspace with status 'available' is treated as
    // 'active' when its module provider is installed. This lets modules
    // appear on the rail only when actually provisioned by the control plane.
    const isWorkspaceActive = useCallback((ws) => {
        return isWorkspaceActiveUtil(ws, isInstalled);
    }, [isInstalled]);

    // Filter workspaces — show all except those gated by moduleKey.
    // The rail/hub further filter using isWorkspaceActive for pinning.
    const visibleWorkspaces = useMemo(() => {
        return enrichedWorkspaces.filter(ws => {
            if (ws.status === 'coming-soon') return true;
            if (ws.permission && (permissionsUnknown || !can(ws.permission))) return false;
            if (ws.moduleKey && !checkModule(ws.moduleKey)) return false;
            return true;
        });
    }, [can, enrichedWorkspaces, checkModule, permissionsUnknown]);

    const [currentWorkspaceId, setCurrentWorkspaceId] = useState(() => {
        try {
            const stored = localStorage.getItem(WORKSPACE_KEY);
            // Accept any stored ID — it may belong to a dynamic workspace
            // that hasn't loaded yet. The workspace-switch guard below
            // will fall back to crm-core if it's invalid.
            if (stored) return stored;
        } catch {}
        return 'crm-core';
    });

    const lastWikiPathRef = useRef(null);
    if (lastWikiPathRef.current === null) {
        try { lastWikiPathRef.current = sessionStorage.getItem(WIKI_LAST_PATH_KEY) || ''; } catch { lastWikiPathRef.current = ''; }
    }

    // Helper: find a workspace by ID in the enriched (merged) list.
    // This replaces the static getWorkspaceById() which only searches
    // the hardcoded WORKSPACES array and would miss dynamic providers.
    const findWorkspaceById = useCallback((id) => {
        return enrichedWorkspaces.find(ws => ws.id === id);
    }, [enrichedWorkspaces]);

    // Auto-detect workspace from URL path.
    // For dynamic module workspaces, we scan the provider-declared navigation
    // paths (section.path and section.items[].path) to find a match.
    // CRM Core is the fallback for all native CRM paths.
    useEffect(() => {
        const path = location.pathname;

        // ProviderShell route — keep current workspace
        if (path.startsWith('/modules')) return;

        // 0. Custom module pages live under /m/<slug> (plan Task 7.9). Matched on the
        //    slug rather than on declared nav paths so a deep link into a page the
        //    manifest does not list in its sidebar still resolves to the right workspace —
        //    a module's own internal routes are its business, and the rail should not go
        //    blank because the user followed one.
        if (path.startsWith('/m/')) {
            const slug = path.split('/')[2];
            const owner = enrichedWorkspaces.find(
                ws => ws.type === 'module-native' && ws.moduleSlug === slug,
            );
            if (owner) {
                setCurrentWorkspaceId(owner.id);
                return;
            }
            // An unknown slug: ModuleHost renders its own "not installed" state, and
            // switching workspace would be misleading. Keep whatever is current.
            return;
        }

        // The built-in Wiki owns its URL space. Check it before provider
        // navigation so a provider cannot capture /wiki by declaring a path.
        if (path.startsWith('/wiki')) {
            setCurrentWorkspaceId('wiki');
            lastWikiPathRef.current = `${path}${location.search || ''}`;
            try { sessionStorage.setItem(WIKI_LAST_PATH_KEY, lastWikiPathRef.current); } catch {}
            return;
        }

        // 1. Check dynamic module workspaces first.
        //    Each module workspace's sections contain navigation paths
        //    declared by the provider's manifest. If the current URL
        //    starts with any of those paths, switch to that workspace.
        let moduleMatch = null;
        let moduleMatchLen = -1;
        for (const ws of enrichedWorkspaces) {
            if (ws.type !== 'module-iframe' || !ws.sections) continue;
            for (const section of ws.sections) {
                // Root-type section: has a direct path
                if (section.path) {
                    const p = section.path;
                    if (p !== '/' && (path === p || path.startsWith(p + '/') || path.startsWith(p))) {
                        if (p.length > moduleMatchLen) {
                            moduleMatch = ws.id;
                            moduleMatchLen = p.length;
                        }
                    }
                }
                // Group-type section: has child items with paths
                if (section.items) {
                    for (const item of section.items) {
                        const p = item.path;
                        if (!p) continue;
                        if (p !== '/' && (path === p || path.startsWith(p + '/') || path.startsWith(p))) {
                            if (p.length > moduleMatchLen) {
                                moduleMatch = ws.id;
                                moduleMatchLen = p.length;
                            }
                        }
                    }
                }
            }
        }
        if (moduleMatch) {
            setCurrentWorkspaceId(moduleMatch);
            return;
        }

        // 2a. Settings virtual workspace — account-level settings routes.
        //     EXCLUDE /settings/team, which is a module-iframe override route
        //     (App.js line ~480) that renders RuntimeShell with no children for
        //     the Sites module. Capturing it here would set the workspace to
        //     'settings' (type: 'native'), causing RuntimeShell to render
        //     {children} (undefined) → blank screen.
        if (path.startsWith('/settings') && !path.startsWith('/settings/team')) {
            setCurrentWorkspaceId('settings');
            return;
        }

        // 2b. Admin virtual workspace — org-level admin routes.
        if (path.startsWith('/admin')) {
            setCurrentWorkspaceId('admin');
            return;
        }

        // 3. Default: CRM Core for all other (native CRM) paths.
        //    If the path doesn't match any module navigation, assume
        //    it belongs to CRM Core (dashboard, contacts, companies, etc.).
        if (path === '/' || path.startsWith('/dashboard') || path.startsWith('/inbox') ||
            path.startsWith('/tasks') || path.startsWith('/contacts') || path.startsWith('/companies') ||
            path.startsWith('/pipeline') || path.startsWith('/lead-pipeline') || path.startsWith('/deals') ||
            path.startsWith('/deal-rooms') || path.startsWith('/deal-registration') || path.startsWith('/proposals') ||
            path.startsWith('/campaigns') || path.startsWith('/events') || path.startsWith('/forms') ||
            path.startsWith('/bookings') || path.startsWith('/activities') ||
            path.startsWith('/reports') || path.startsWith('/ai-') || path.startsWith('/agents') ||
            path.startsWith('/settings/team') || /* /settings/team without Sites module falls back to crm-core */
            path.startsWith('/help') || path.startsWith('/feature-requests') || path.startsWith('/release-notes') ||
            path.startsWith('/email-sequences') || path.startsWith('/products') || path.startsWith('/list-management') ||
            path.startsWith('/booking-links') || path.startsWith('/communications') ||
            path.startsWith('/onboarding') || path.startsWith('/testimonials')) {
            setCurrentWorkspaceId('crm-core');
        }
    }, [location.pathname, location.search, enrichedWorkspaces]);

    // Hydrate moduleNavTarget from URL on mount (handles page refresh at deep module paths)
    // Without this, refreshing at a deep module path (e.g. /sites/create) leaves
    // moduleNavTarget=null, so the iframe loads the base URL without the deep
    // link and the sidebar highlight is wrong.
    useEffect(() => {
        const path = location.pathname;
        // If the current workspace is a module-iframe workspace and the URL
        // is a deep path within that module, set the nav target so the iframe
        // boots to the correct page.
        if (path !== '/' && path.split('/').length > 2) {
            const ws = findWorkspaceById(currentWorkspaceId);
            if (ws?.type === 'module-iframe' && ws.sections) {
                // Check if the path matches any of this workspace's nav paths
                const matchesModule = ws.sections.some(section => {
                    if (section.path && path.startsWith(section.path)) return true;
                    return (section.items || []).some(item => item.path && path.startsWith(item.path));
                });
                if (matchesModule) {
                    setModuleNavTarget({ path, nonce: Date.now() });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Persist active workspace
    useEffect(() => {
        try { localStorage.setItem(WORKSPACE_KEY, currentWorkspaceId); } catch {}
    }, [currentWorkspaceId]);

    const currentWorkspace = useMemo(() => {
        return enrichedWorkspaces.find(w => w.id === currentWorkspaceId) || enrichedWorkspaces[0];
    }, [currentWorkspaceId, enrichedWorkspaces]);

    const navigateModule = useCallback((path) => {
        setModuleNavTarget({ path, nonce: Date.now() });
        // Also update the browser URL so back/forward navigation works
        if (path && path !== location.pathname) {
            navigate(path);
        }
    }, [navigate, location.pathname]);

    const setCurrentWorkspace = useCallback((workspaceId) => {
        // Look up the workspace in the enriched (merged) list, which
        // includes both static native workspaces and dynamic module
        // workspaces generated from the API response.
        const ws = findWorkspaceById(workspaceId);
        if (!ws) return;
        // Allow both statically active and dynamically promoted workspaces
        if (!isWorkspaceActive(ws)) return;
        setCurrentWorkspaceId(workspaceId);

        if (workspaceId === 'wiki' && lastWikiPathRef.current) {
            if (lastWikiPathRef.current !== `${location.pathname}${location.search || ''}`) {
                navigate(lastWikiPathRef.current);
            }
            return;
        }

        // For native workspaces, navigate to the first sidebar item.
        // For module-iframe workspaces, auto-navigate to the first nav item
        // so the iframe loads the provider's home page immediately.
        if (ws.type === 'native' && ws.sections.length > 0 && ws.sections[0].items.length > 0) {
            const firstPath = ws.sections[0].items[0].path;
            if (firstPath && firstPath !== location.pathname) {
                navigate(firstPath);
            }
        } else if (ws.type === 'module-native') {
            // A React Router navigation, not `navigateModule` — a custom module is a real
            // route in this app (`/m/:slug/*`), not an iframe told where to go by
            // postMessage. `basePath` is the fallback so a module whose manifest declares
            // no sidebar items still has somewhere to land.
            const firstPath = ws.sections?.[0]?.items?.[0]?.path || ws.basePath;
            if (firstPath && firstPath !== location.pathname) {
                navigate(firstPath);
            }
        } else if (ws.type === 'module-iframe') {
            // Find the first nav section with a path (root-type item)
            const enrichedWs = enrichedWorkspaces.find(w => w.id === workspaceId);
            if (enrichedWs?.sections?.length > 0) {
                const firstSection = enrichedWs.sections[0];
                // Root items have a direct path; group items have child items
                const firstPath = firstSection.path || firstSection.items?.[0]?.path;
                if (firstPath) {
                    navigateModule(firstPath);
                }
            }
        }
    }, [navigate, location.pathname, location.search, isWorkspaceActive, enrichedWorkspaces, navigateModule, findWorkspaceById]);

    // Check if a sidebar item is visible (feature gating, admin gating).
    //
    // Every applicable condition is evaluated — they are NOT alternatives. The
    // previous shape returned early on `adminOnly` and `agentDeveloper`, so any
    // item carrying one of those *and* a `feature` skipped the plan gate
    // entirely. Three items did: `ai-heartbeat` (adminOnly + ai_heartbeat),
    // `list-management` (adminOnly + list_management), and `ai-agents`
    // (agentDeveloper, whose route had no gate at all). An admin on a plan
    // without AI Heartbeat still saw the nav item, and clicking it hit an
    // endpoint that — since the plan gating work — now answers 403.
    const isItemVisible = useCallback((item, user) => {
        if (item.permission && (permissionsUnknown || !can(item.permission))) return false;
        if (item.adminOnly && !user?.is_admin) return false;
        if (item.agentDeveloper && !(user?.is_agent_developer || user?.is_admin)) return false;
        // While the plan is still loading, leave feature-gated items in place
        // rather than hiding them: `checkFeature` fails closed, so denying here
        // would blank half the sidebar on every page load and then repopulate
        // it. The nav is a hint; the API is the enforcement, and every
        // `/api/ai/*` route is gated server-side.
        if (item.feature && !planUnknown && !checkFeature(item.feature)) return false;
        return true;
    }, [can, checkFeature, permissionsUnknown, planUnknown]);

    const value = useMemo(() => ({
        currentWorkspace,
        currentWorkspaceId,
        setCurrentWorkspace,
        visibleWorkspaces,
        isItemVisible,
        isWorkspaceActive,
        isInstalled,
        getHealthStatus,
        providers,
        // The raw custom-module payloads, not just their workspaces: ModuleHost needs
        // `entry_url`, `style_urls` and `api_base`, which are delivery facts rather than
        // navigation, and putting them on the workspace object would mix the registry
        // shape with the loader's output.
        customModules,
        loading,
        moduleNavTarget,
        navigateModule,
    }), [currentWorkspace, currentWorkspaceId, setCurrentWorkspace, visibleWorkspaces, isItemVisible, isWorkspaceActive, isInstalled, getHealthStatus, providers, customModules, loading, moduleNavTarget, navigateModule]);

    return (
        <WorkspaceContext.Provider value={value}>
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
    return ctx;
}
